import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt, FaCalendarAlt } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import AnimatedSection from '../Animation/AnimatedSection';

const Section = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.theme.colors.secondary};
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const Subtitle = styled(motion.p)`
  text-align: center;
  color: ${props => props.theme.colors.grayMedium};
  max-width: 700px;
  margin: 0 auto 4rem;
  font-size: 1.2rem;
  line-height: 1.6;
`;

const InmetroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
  align-items: stretch;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled(motion.article)`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 2rem;
  box-shadow: ${props => props.theme.boxShadow};

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    -webkit-line-clamp: 5;
    min-height: auto;
  }
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    min-height: 40vh;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: 30vh;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    min-height: 57vh;
  }
`;

const PostDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.grayMedium};
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 3.6em;
`;

const Excerpt = styled.p`
  color: ${props => props.theme.colors.grayDark};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 2rem;

  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  flex-grow: 1;
  min-height: 6.4em;
`;

const ReadMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const CategoryTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  font-size: 1.8rem;
  font-weight: ${props => props.theme.fontWeights.bold};
  text-align: left;
  margin: 3rem 0 2rem;
  padding-left: 1rem;
  padding-bottom: 0.5rem;
  border-left: 4px solid ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-style: italic;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
     font-size: 1.5rem;
   }
  
  &:after {
    content: '';
    position: absolute;
    left: 1rem;
    bottom: 0;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const truncate = (text, max = 180) => {
  if (!text) return '';
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
};

const mockPosts = [
  {
    title: 'Inmetro fiscaliza mais de 25 mil bebidas e encontra produtos com quantidade inferior ao informado no rótulo',
    excerpt:
      'Instituto alerta para práticas que prejudicam consumidores no período das festas de fim de ano.',
    date: '2025-12-15',
    link: 'https://www.gov.br/inmetro/pt-br/centrais-de-conteudo/noticias/inmetro-fiscaliza-mais-de-25-mil-bebidas-e-encontra-produtos-com-volume-abaixo-do-informado-no-rotulo'
  },
  {
    title: 'Servidores do Inmetro recebem kits de Natal na 16ª Comemoração da Entrega do Kit de Natal, promovida pelo Asmetro-SI',
    excerpt:
      'Ação incluiu momento de homenagens a servidores que contribuíram para o fortalecimento do Inmetro ao longo de seus 52 anos de história.',
    date: '2025-12-16',
    link: 'https://www.gov.br/inmetro/pt-br/centrais-de-conteudo/noticias/servidores-do-inmetro-recebem-kits-de-natal-na-16a-comemoracao-da-entrega-do-kit-de-natal-promovida-pelo-asmetro-si'
  },
  {
    title: 'Inmetro identifica irregularidades em mais de 90 mil brinquedos, luminárias e alimentos típicos de festas de fim de ano',
    excerpt:
      'Ação nacional fiscalizou 725 mil produtos e identificou, como principal irregularidade, a comercialização de brinquedos sem registro obrigatório.',
    date: '2025-12-16',
    link: 'https://www.gov.br/inmetro/pt-br/centrais-de-conteudo/noticias/natal-seguro-inmetro-identifica-irregularidades-em-mais-de-90-mil-brinquedos-luminarias-e-alimentos-tipicos'
  }
];

const linkLabel = (url = '') => (url.includes('/anatel/') ? 'Abrir na Anatel' : 'Ler no INMETRO');

const parseRdfFeed = (xmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'application/xml');
  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) throw new Error('Falha ao parsear XML do RSS');

  const items = Array.from(doc.getElementsByTagName('item'));
  const dcNS = 'http://purl.org/dc/elements/1.1/';

  return items.map((item) => {
    const title = item.getElementsByTagName('title')[0]?.textContent || '';
    const link = item.getElementsByTagName('link')[0]?.textContent || '';
    const description = item.getElementsByTagName('description')[0]?.textContent || '';

    let dateText = '';
    try {
      dateText = item.getElementsByTagNameNS(dcNS, 'date')[0]?.textContent || '';
    } catch (_) {
      dateText = item.getElementsByTagName('dc:date')[0]?.textContent || '';
    }

    let dateISO = '';
    if (dateText) {
      const d = new Date(dateText);
      dateISO = isNaN(d.getTime()) ? '' : d.toISOString();
    }

    return {
      title,
      link,
      excerpt: truncate(description),
      date: dateISO || new Date().toISOString(),
    };
  });
};

const selectTopPosts = (posts, count = 3) => {
  const sorted = [...posts].sort((a, b) => {
    const da = new Date(a.date);
    const db = new Date(b.date);
    return db - da;
  });

  const withDescription = sorted.filter(post => {
    if (!post.excerpt) return false;
    const text = post.excerpt.trim();
    return text.length > 0 && text !== '…';
  });

  const selected = withDescription.slice(0, count);

  if (selected.length < count) {
    for (const post of sorted) {
      const alreadyIncluded = selected.some(p => p.link === post.link);
      if (alreadyIncluded) continue;

      selected.push(post);
      if (selected.length === count) break;
    }
  }

  return selected;
};

const buildProxyUrls = (url) => [
  `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  `https://cors.isomorphic-git.org/${url}`,
];

const fetchViaProxies = async (url) => {
  const candidates = buildProxyUrls(url);
  for (const endpoint of candidates) {
    try {
      const res = await fetch(endpoint);
      if (!res.ok) continue;
      const xml = await res.text();
      return xml;
    } catch (_) {
      // tenta próximo proxy
    }
  }
  throw new Error('Falha em todos os proxies');
};

const Blog = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [anatelPosts, setAnatelPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRss = async () => {
      setLoading(true);
      setError(null);
      const RSS_URL = 'https://www.gov.br/anatel/RSS';

      try {
        const xml = await fetchViaProxies(RSS_URL);
        const parsedPosts = parseRdfFeed(xml);
        const finalPosts = selectTopPosts(parsedPosts, 3);
        if (!cancelled) setAnatelPosts(finalPosts);
      } catch (err) {
        if (!cancelled) setError('Não foi possível carregar o RSS da Anatel.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRss();
    return () => { cancelled = true; };
  }, []);

  return (
    <Section id="blog">
      <div className="container">
        <SectionTitle
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Blog
        </SectionTitle>

        <AnimatedSection animation="fadeIn" delay={0.2}>
          <Subtitle>
            Acompanhe as últimas atualizações e notícias do setor.
          </Subtitle>
        </AnimatedSection>

        {/* Anatel */}
        {loading && (
          <AnimatedSection animation="fadeIn" delay={0.3}>
            <Subtitle>Carregando notícias da Anatel…</Subtitle>
          </AnimatedSection>
        )}

        {error && (
          <AnimatedSection animation="fadeIn" delay={0.3}>
            <Subtitle>{error}</Subtitle>
          </AnimatedSection>
        )}

        {!loading && anatelPosts.length > 0 && (
          <>
            <AnimatedSection animation="fadeInUp" delay={0.3}>
              <CategoryTitle
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                Anatel
              </CategoryTitle>
            </AnimatedSection>

            <InmetroGrid>
              {anatelPosts.map((post, index) => (
                <AnimatedSection
                  key={`anatel-${index}`}
                  animation="fadeIn"
                  delay={0.35 + (0.08 * index)}
                  duration={0.6}
                >
                  <Card
                    whileHover={{
                      y: -8,
                      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)'
                    }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div>
                      <PostDate>
                        <FaCalendarAlt />
                        {new Date(post.date).toLocaleDateString('pt-BR')}
                      </PostDate>

                      <CardTitle>{post.title}</CardTitle>
                      <Excerpt>
                        {post.excerpt?.trim()
                          ? post.excerpt
                          : 'Essa publicação não possui descrição. Clique no link para mais detalhes.'}
                      </Excerpt>
                    </div>

                    <ReadMore href={post.link} target="_blank" rel="noopener noreferrer">
                      {linkLabel(post.link)} <FaExternalLinkAlt />
                    </ReadMore>
                  </Card>
                </AnimatedSection>
              ))}
            </InmetroGrid>
          </>
        )}

        {/* Inmetro */}
        <AnimatedSection animation="fadeInUp" delay={0.6}>
          <CategoryTitle
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Inmetro
          </CategoryTitle>
        </AnimatedSection>

        <InmetroGrid>
          {mockPosts.map((post, index) => (
            <AnimatedSection
              key={`inmetro-${index}`}
              animation="fadeIn"
              delay={0.65 + (0.08 * index)}
              duration={0.6}
            >
              <Card
                whileHover={{
                  y: -8,
                  boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)'
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div>
                  <PostDate>
                    <FaCalendarAlt />
                    {new Date(post.date).toLocaleDateString('pt-BR')}
                  </PostDate>

                  <CardTitle>{post.title}</CardTitle>
                  <Excerpt>{post.excerpt || 'Sem descrição.'}</Excerpt>
                </div>

                <ReadMore href={post.link} target="_blank" rel="noopener noreferrer">
                  {linkLabel(post.link)} <FaExternalLinkAlt />
                </ReadMore>
              </Card>
            </AnimatedSection>
          ))}
        </InmetroGrid>
      </div>
    </Section>
  );
};

export default Blog;
